import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Row,
  Col,
  Select,
  Switch,
  Button,
  message,
  Spin,
  Empty,
  Tag,
  Collapse,
  Tooltip,
} from 'antd';
import {
  SaveOutlined,
  CopyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faUser,
  faShieldHalved,
  faNotesMedical,
  faCalendarCheck,
} from '@fortawesome/free-solid-svg-icons';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import FormConfigService from '../../services/FormConfigService';
import BookingService from '../../services/BookingService';
import {
  PageContainer,
  ConfigCard,
  SectionHeader,
  SectionIcon,
  SectionCollapseWrapper,
  FieldRow,
  FieldLabel,
  FieldControls,
  PhaseTag,
  EventSelectorCard,
} from './styled';

const PHASE_OPTIONS = [
  { label: 'Onsite', value: 'onsite' },
  { label: 'Deferred', value: 'deferred' },
  { label: 'Both', value: 'both' },
];

// Fields that are always enabled + required (cannot be toggled off)
const LOCKED_FIELDS = new Set(['firstName', 'lastName', 'emailAddress', 'selectedEvent', 'dateOfBirth', 'usedBefore']);

// Sections that should not appear on the FormConfig admin page
// (staff-only / system-managed sections)
const HIDDEN_ADMIN_SECTIONS = new Set([
  'vitalSigns',
  'bodyMeasurements',
  'bloodTests',
  'additionalTests',
  'spouseInfo',
  'riskAssessment',
]);

const SECTION_ICONS = {
  patientInfo: faUser,
  demographics: faCalendarCheck,
  medicalHistory: faNotesMedical,
  consents: faShieldHalved,
};

const FormConfig = observer(() => {
  const { setPageHeader } = usePageHeader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('default');
  const [config, setConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Set page header
  useEffect(() => {
    setPageHeader({ title: 'Form Configuration' });
  }, [setPageHeader]);

  // Load events for the dropdown
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await BookingService.getAllBookings({ 
          limit: 200, 
          sortField: 'bookingDate',
          sortOrder: 'asc',
        });
        if (response?.success) {
          // Handle various response shapes from BookingService
          const data = response.data;
          let bookings = [];
          if (Array.isArray(data)) {
            bookings = data;
          } else if (Array.isArray(data?.bookings)) {
            bookings = data.bookings;
          } else if (Array.isArray(data?.data)) {
            bookings = data.data;
          }
          // Filter out past events and sort soonest first
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          bookings = bookings
            .filter(b => {
              const eventDate = new Date(b.endDate || b.bookingDate || 0);
              return eventDate >= now;
            })
            .sort((a, b) => {
              const dateA = new Date(a.bookingDate || 0);
              const dateB = new Date(b.bookingDate || 0);
              return dateA - dateB;
            });
          setEvents(bookings);
        }
      } catch (err) {
        console.error('Failed to load events:', err);
      }
    };
    fetchEvents();
  }, []);

  // Load config when event selection changes
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      let result;
      if (selectedBookingId === 'default') {
        result = await FormConfigService.getDefault();
      } else {
        result = await FormConfigService.getByBookingId(selectedBookingId);
      }
      setConfig(result);
      setHasChanges(false);
    } catch (err) {
      // If no event-specific config, offer to clone
      if (err.message?.includes('404') || err.status === 404) {
        setConfig(null);
      } else {
        message.error('Failed to load form configuration');
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedBookingId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Clone default config for selected event
  const handleCloneForEvent = async () => {
    if (selectedBookingId === 'default') return;
    setLoading(true);
    try {
      const result = await FormConfigService.cloneForBooking(selectedBookingId);
      setConfig(result);
      setHasChanges(false);
      message.success('Configuration cloned from default');
    } catch (err) {
      message.error('Failed to clone configuration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Update a section's enabled state
  const handleSectionToggle = (sectionKey, enabled) => {
    if (!config) return;
    const updated = { ...config };
    updated.sections = updated.sections.map(s =>
      s.key === sectionKey ? { ...s, enabled } : s
    );
    // Also toggle all fields in this section
    updated.fields = updated.fields.map(f =>
      f.section === sectionKey ? { ...f, enabled } : f
    );
    setConfig(updated);
    setHasChanges(true);
  };

  // Update a field's enabled state
  const handleFieldToggle = (fieldName, enabled) => {
    if (!config) return;
    const updated = { ...config };
    updated.fields = updated.fields.map(f =>
      f.name === fieldName ? { ...f, enabled } : f
    );
    setConfig(updated);
    setHasChanges(true);
  };

  // Update a field's required state
  const handleFieldRequired = (fieldName, required) => {
    if (!config) return;
    const updated = { ...config };
    updated.fields = updated.fields.map(f =>
      f.name === fieldName ? { ...f, required } : f
    );
    setConfig(updated);
    setHasChanges(true);
  };

  // Update a field's phase
  const handleFieldPhase = (fieldName, phase) => {
    if (!config) return;
    const updated = { ...config };
    updated.fields = updated.fields.map(f =>
      f.name === fieldName ? { ...f, phase } : f
    );
    setConfig(updated);
    setHasChanges(true);
  };

  // Save
  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      if (selectedBookingId === 'default') {
        await FormConfigService.updateById(config._id, {
          sections: config.sections,
          fields: config.fields,
        });
      } else {
        await FormConfigService.saveForBooking(selectedBookingId, {
          sections: config.sections,
          fields: config.fields,
        });
      }
      setHasChanges(false);
      message.success('Configuration saved successfully');
    } catch (err) {
      message.error('Failed to save configuration');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Group fields by section
  const fieldsBySection = {};
  if (config?.fields) {
    for (const field of config.fields) {
      if (!fieldsBySection[field.section]) {
        fieldsBySection[field.section] = [];
      }
      fieldsBySection[field.section].push(field);
    }
  }

  // Sort sections by order, filtering out staff-only / system sections
  const sortedSections = config?.sections
    ? [...config.sections]
        .filter(s => !HIDDEN_ADMIN_SECTIONS.has(s.key))
        .sort((a, b) => a.order - b.order)
    : [];

  return (
    <PageContainer>
      {/* Event Selector */}
      <EventSelectorCard>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Select
              style={{ width: '100%' }}
              placeholder="Select an event or use default"
              value={selectedBookingId}
              onChange={setSelectedBookingId}
              showSearch
              optionFilterProp="children"
              size="large"
            >
              <Select.Option value="default">
                <FontAwesomeIcon icon={faCog} style={{ marginRight: 8, color: '#999' }} />
                Default Configuration (applies to all events)
              </Select.Option>
              {events.map(event => {
                const eventName = event.eventLocation?.eventName || event.title || event.name || event.eventType?.name || 'Untitled Event';
                const date = event.bookingDate || event.eventDate;
                const dateStr = date
                  ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '';
                const timeStr = date
                  ? new Date(date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                  : '';
                const location = event.eventLocation?.venueName || '';

                return (
                  <Select.Option key={event._id} value={event._id}>
                    {eventName}{dateStr ? ` — ${dateStr}` : ''}{timeStr ? ` at ${timeStr}` : ''}{location ? ` (${location})` : ''}
                  </Select.Option>
                );
              })}
            </Select>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges || !config}
              size="large"
            >
              Save
            </Button>
          </Col>
          <Col>
            <Tooltip title="Reload configuration">
              <Button
                icon={<ReloadOutlined />}
                onClick={loadConfig}
                size="large"
              />
            </Tooltip>
          </Col>
        </Row>
      </EventSelectorCard>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Spin size="large" />
        </div>
      )}

      {/* No config for this event — offer to clone */}
      {!loading && !config && selectedBookingId !== 'default' && (
        <ConfigCard>
          <Empty
            description="No custom configuration for this event"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={handleCloneForEvent}>
              <CopyOutlined /> Clone from Default
            </Button>
          </Empty>
        </ConfigCard>
      )}

      {/* Sections & Fields */}
      {!loading && config && (
        <SectionCollapseWrapper>
          <Collapse
            defaultActiveKey={sortedSections.map(s => s.key)}
            ghost
          >
            {sortedSections.map(section => {
              const sectionFields = (fieldsBySection[section.key] || [])
                .sort((a, b) => a.order - b.order);
              const enabledCount = sectionFields.filter(f => f.enabled).length;

              return (
                <Collapse.Panel
                  key={section.key}
                  header={
                    <SectionHeader onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <Switch
                          size="small"
                          checked={section.enabled}
                          onChange={(checked) => handleSectionToggle(section.key, checked)}
                        />
                        <SectionIcon>
                          <FontAwesomeIcon icon={SECTION_ICONS[section.key] || faCog} />
                        </SectionIcon>
                        <span style={{ fontWeight: 600, fontSize: 15, color: '#1a1a1a' }}>
                          {section.label}
                        </span>
                        <Tag
                          style={{
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 500,
                            padding: '1px 8px',
                          }}
                        >
                          {enabledCount}/{sectionFields.length} fields
                        </Tag>
                      </div>
                    </SectionHeader>
                  }
                >
                  {sectionFields.map(field => {
                    const isLocked = LOCKED_FIELDS.has(field.name);
                    return (
                      <FieldRow key={field.name}>
                        <FieldLabel>
                          {field.label}
                          {(field.required || isLocked) && <span style={{ color: '#ff4d4f', marginLeft: 4, fontWeight: 600 }}>*</span>}
                          {isLocked && <span style={{ color: '#999', fontSize: 11, marginLeft: 8, fontStyle: 'italic' }}>Always required</span>}
                        </FieldLabel>
                        <FieldControls>
                          <PhaseTag $phase={field.phase}>{field.phase}</PhaseTag>
                          <Select
                            size="small"
                            value={field.phase}
                            options={PHASE_OPTIONS}
                            onChange={(value) => handleFieldPhase(field.name, value)}
                            style={{ width: 110 }}
                            disabled={!field.enabled || isLocked}
                          />
                          <Tooltip title={isLocked ? 'Required (locked)' : 'Required'}>
                            <Switch
                              size="small"
                              checked={field.required || isLocked}
                              onChange={(checked) => handleFieldRequired(field.name, checked)}
                              disabled={!field.enabled || isLocked}
                              checkedChildren="Req"
                              unCheckedChildren="Opt"
                            />
                          </Tooltip>
                          <Tooltip title={isLocked ? 'This field cannot be disabled' : ''}>
                            <Switch
                              size="small"
                              checked={field.enabled || isLocked}
                              onChange={(checked) => handleFieldToggle(field.name, checked)}
                              disabled={isLocked}
                            />
                          </Tooltip>
                        </FieldControls>
                      </FieldRow>
                    );
                  })}
                </Collapse.Panel>
              );
            })}
          </Collapse>
        </SectionCollapseWrapper>
      )}
    </PageContainer>
  );
});

export default FormConfig;
